/**
 * Ordered by source, then by the order an admin meets them in the seed policy, because the
 * "Available inputs" panel renders the list exactly as it stands here.
 */
export const INPUT_CATALOG = [
    // ── Vehicle and estimate facts ────────────────────────────────────────────────────────────────
    {
        name: 'valuationYear',
        unit: 'years',
        source: 'VEHICLE',
        label: 'Valuation year',
        description: 'The year of the valuation date — the saved date, never the day a record is reopened (PP §7.1).',
    },
    { name: 'modelYear', unit: 'years', source: 'VEHICLE', label: 'Model year', description: 'The vehicle model year as recorded.' },
    {
        name: 'referencePrice',
        unit: 'JOD',
        source: 'VEHICLE',
        label: 'Reference price',
        description: 'The dated official reference price for this model, defaulted from the template version.',
    },
    { name: 'odometer', unit: 'km', source: 'VEHICLE', label: 'Odometer', description: 'Recorded mileage in kilometres.' },
    { name: 'ownerCount', unit: 'count', source: 'VEHICLE', label: 'Owners', description: 'Number of previous owners on the licence.' },
    {
        name: 'registrationDaysDifference',
        unit: 'days',
        source: 'VEHICLE',
        label: 'Days to registration expiry',
        description: 'Calendar days from the valuation date to the registration expiry; negative when it has already expired.',
    },
    {
        name: 'chassisRate',
        unit: 'fraction',
        source: 'VEHICLE',
        label: 'Chassis (سبعة جيد) rate',
        description: 'The deduction the recorded grade maps to in the template version’s grade table.',
    },
    {
        name: 'interiorRate',
        unit: 'fraction',
        source: 'VEHICLE',
        label: 'Interior rate',
        description: 'The recorded interior severity, priced by the template version’s severity table.',
    },
    {
        name: 'engineRate',
        unit: 'fraction',
        source: 'VEHICLE',
        label: 'Engine rate',
        description: 'The recorded engine severity, priced by the template version’s severity table.',
    },
    {
        name: 'hybridRate',
        unit: 'fraction',
        source: 'VEHICLE',
        label: 'Hybrid / EV rate',
        description: 'The recorded hybrid or EV severity. Zero and marked "not applicable" for a petrol or diesel vehicle (PP §7.5).',
    },
    {
        name: 'tireRate',
        unit: 'fraction',
        source: 'VEHICLE',
        label: 'Tyres rate',
        description: 'The recorded tyre severity, priced by the template version’s severity table.',
    },
    {
        name: 'damageGroup1',
        unit: 'JOD',
        source: 'VEHICLE',
        label: 'Damage group 1 deduction',
        description: 'The fixed JOD effect of the one priced selection from the first Arabic damage group.',
    },
    {
        name: 'damageGroup2',
        unit: 'JOD',
        source: 'VEHICLE',
        label: 'Damage group 2 deduction',
        description: 'The fixed JOD effect of the one priced selection from the second Arabic damage group.',
    },
    {
        name: 'faceliftRate',
        unit: 'fraction',
        source: 'VEHICLE',
        label: 'Facelift deduction',
        description: 'The template’s facelift rate when facelift applies to this vehicle, otherwise zero. Unknown is not No (PP §7.1).',
    },
    {
        name: 'customsImpactRate',
        unit: 'fraction',
        source: 'VEHICLE',
        label: 'Customs market impact',
        description: 'The template’s customs market-value adjustment when it applies, otherwise zero. This is not a tax charge.',
    },
    { name: 'spareKeyValue', unit: 'JOD', source: 'VEHICLE', label: 'Spare key value', description: 'Template value when a spare key is present.' },
    {
        name: 'ownersManualValue',
        unit: 'JOD',
        source: 'VEHICLE',
        label: 'Owner’s manual value',
        description: 'Template value when the owner’s manual is present.',
    },
    { name: 'toolKitValue', unit: 'JOD', source: 'VEHICLE', label: 'Tool kit value', description: 'Template value when the tool kit is present.' },
    {
        name: 'spareTireValue',
        unit: 'JOD',
        source: 'VEHICLE',
        label: 'Spare tyre value',
        description: 'Template value when the spare tyre is present.',
    },
    {
        name: 'acquisitionBasis',
        unit: 'JOD',
        source: 'VEHICLE',
        label: 'Acquisition basis',
        description: 'What CTA pays: the offer on an open estimate, the actual purchase price on a stock vehicle (plan §5.2).',
    },
    {
        name: 'taxTrafficEstimate',
        unit: 'JOD',
        source: 'VEHICLE',
        label: 'Tax / traffic estimate',
        description: 'Recorded tax and traffic charge for this vehicle, kept apart from the customs market adjustment.',
    },
    {
        name: 'otherDirectCostEstimate',
        unit: 'JOD',
        source: 'VEHICLE',
        label: 'Other direct costs',
        description: 'Any other direct cost recorded against this vehicle that no other category covers.',
    },
    {
        name: 'useGrossMargin',
        unit: 'flag',
        source: 'VEHICLE',
        label: 'Use gross margin',
        description: '1 when the record prices on gross margin, 0 when it prices on a fixed profit. Never both (PP §7.2).',
    },
    // ── Template shared values ────────────────────────────────────────────────────────────────────
    { name: 'depYear1', unit: 'fraction', source: 'TEMPLATE', label: 'Year 1 depreciation', description: 'First-year depreciation rate.' },
    { name: 'depYear2', unit: 'fraction', source: 'TEMPLATE', label: 'Year 2 depreciation', description: 'Second-year depreciation rate.' },
    { name: 'depYear3', unit: 'fraction', source: 'TEMPLATE', label: 'Year 3 depreciation', description: 'Third-year depreciation rate.' },
    {
        name: 'depYear4',
        unit: 'fraction',
        source: 'TEMPLATE',
        label: 'Year 4+ depreciation',
        description: 'The fourth-year increment; the seed policy stops adding after year four.',
    },
    {
        name: 'annualMileageAllowance',
        unit: 'km/year',
        source: 'TEMPLATE',
        label: 'Annual mileage allowance',
        description: 'Expected kilometres per year of age.',
    },
    { name: 'mileageStepKm', unit: 'km', source: 'TEMPLATE', label: 'Excess mileage step', description: 'The size of one excess-mileage step.' },
    {
        name: 'mileageStepRate',
        unit: 'fraction',
        source: 'TEMPLATE',
        label: 'Penalty per mileage step',
        description: 'Deduction applied per started excess-mileage step.',
    },
    {
        name: 'ownerAllowance',
        unit: 'count',
        source: 'TEMPLATE',
        label: 'Owner allowance',
        description: 'Owners allowed before the owner penalty starts.',
    },
    {
        name: 'ownerStepRate',
        unit: 'fraction',
        source: 'TEMPLATE',
        label: 'Penalty per extra owner',
        description: 'Deduction per owner above the allowance.',
    },
    {
        name: 'roundingIncrement',
        unit: 'JOD',
        source: 'TEMPLATE',
        label: 'Commercial rounding increment',
        description: 'The increment appraisals, ceilings and retail prices round to (PP §7.2).',
    },
    {
        name: 'registrationThresholdDays',
        unit: 'days',
        source: 'TEMPLATE',
        label: 'Registration threshold',
        description: 'Below this many remaining days the licensing, general and registration fees apply.',
    },
    { name: 'licensingRate', unit: 'fraction', source: 'TEMPLATE', label: 'Licensing rate', description: 'Licensing estimate as a rate of value.' },
    {
        name: 'ownershipTransferRate',
        unit: 'fraction',
        source: 'TEMPLATE',
        label: 'Ownership-transfer rate',
        description: 'Transfer fee as a rate of value.',
    },
    { name: 'insuranceRate', unit: 'fraction', source: 'TEMPLATE', label: 'Insurance rate', description: 'Insurance as a rate of value.' },
    { name: 'insuranceCap', unit: 'JOD', source: 'TEMPLATE', label: 'Insurance cap', description: 'Upper bound on the insurance estimate.' },
    { name: 'overheadRate', unit: 'fraction', source: 'TEMPLATE', label: 'Overhead rate', description: 'Overhead as a rate of value.' },
    {
        name: 'fuelPricePerLiter',
        unit: 'JOD/L',
        source: 'TEMPLATE',
        label: 'Fuel price per litre',
        description: 'Price per litre used for the fuel cost line.',
    },
    {
        name: 'dailyHoldingRate',
        unit: 'JOD/day',
        source: 'TEMPLATE',
        label: 'Daily holding rate',
        description: 'Cost of holding the vehicle for one day. Seeded at 0 — the rate is unapproved (PP §7.3).',
    },
    // ── Template values the record may override ───────────────────────────────────────────────────
    { name: 'generalFee', unit: 'JOD', source: 'OVERRIDABLE', label: 'General fees', description: 'Fixed general fee when registration applies.' },
    {
        name: 'registrationFee',
        unit: 'JOD',
        source: 'OVERRIDABLE',
        label: 'Registration fee',
        description: 'Fixed registration fee when registration applies.',
    },
    { name: 'commissionFee', unit: 'JOD', source: 'OVERRIDABLE', label: 'Sales commission', description: 'Commission allowed for this vehicle.' },
    { name: 'fuelLiters', unit: 'L', source: 'OVERRIDABLE', label: 'Fuel litres', description: 'Litres of fuel costed to this vehicle.' },
    {
        name: 'expectedHoldingDays',
        unit: 'days',
        source: 'OVERRIDABLE',
        label: 'Expected holding days',
        description: 'How long the vehicle is expected to sit in stock before it sells.',
    },
    { name: 'targetProfit', unit: 'JOD', source: 'OVERRIDABLE', label: 'Target profit', description: 'Fixed profit target in JOD.' },
    { name: 'targetMargin', unit: 'fraction', source: 'OVERRIDABLE', label: 'Target gross margin', description: 'Gross-margin target as a fraction.' },
    { name: 'riskBuffer', unit: 'JOD', source: 'OVERRIDABLE', label: 'Risk buffer', description: 'Buffer deducted from the acquisition ceiling.' },
    // ── Supplied by the platform ──────────────────────────────────────────────────────────────────
    {
        name: 'reconditioningResolved',
        unit: 'JOD',
        source: 'SYSTEM',
        label: 'Resolved reconditioning',
        description: 'The resolved total of the RECONDITIONING cost items — actuals where they exist, estimates where they do not (plan §5.5).',
    },
    {
        name: 'daysInStock',
        unit: 'days',
        source: 'SYSTEM',
        label: 'Days in stock',
        description: 'Days since the purchase date; 0 on an estimate, frozen at the sale date once sold.',
    },
    { name: 'isSold', unit: 'flag', source: 'SYSTEM', label: 'Sold', description: '1 once the vehicle has been sold, otherwise 0.' },
    // ── Trade-in only ─────────────────────────────────────────────────────────────────────────────
    {
        name: 'newVehicleMargin',
        unit: 'fraction',
        source: 'TRADE_IN',
        label: 'New-vehicle margin',
        description: 'Margin on the new vehicle being sold alongside the trade-in, with its dated source.',
    },
    { name: 'newVehicleProfit', unit: 'JOD', source: 'TRADE_IN', label: 'New-vehicle profit', description: 'Profit amount on the new vehicle.' },
    {
        name: 'demandFactor',
        unit: 'fraction',
        source: 'TRADE_IN',
        label: 'Demand factor',
        description: 'The template value for the recorded used-car demand level.',
    },
    {
        name: 'stockFactor',
        unit: 'fraction',
        source: 'TRADE_IN',
        label: 'Stock factor',
        description: 'The template value for the recorded used-car stock level.',
    },
    { name: 'supportCap', unit: 'JOD', source: 'TRADE_IN', label: 'Support cap', description: 'The finance-approved cap on trade-in support.' },
];
const BY_NAME = new Map(INPUT_CATALOG.map((entry) => [entry.name, entry]));
export const findInput = (name) => BY_NAME.get(name);
export const INPUT_NAMES = INPUT_CATALOG.map((entry) => entry.name);
/** `TRADE_IN` inputs may only be read by `TRADE_IN`-scoped equations (plan §5.1 `SCOPE_LEAK`). */
export const isTradeInInput = (name) => BY_NAME.get(name)?.source === 'TRADE_IN';
export const inputsBySource = (source) => INPUT_CATALOG.filter((entry) => entry.source === source);
//# sourceMappingURL=input-catalog.js.map